'use client';

import { useTranslation } from 'react-i18next';
import { LanguageToggle } from '@/components/LanguageToggle';
import { Badge } from '@/components/ds/Badge';

// ---------------------------------------------------------------------------
// Pathname → i18n key mapping
// Order matters: more specific paths first; /dashboard last (catch-all)
// ---------------------------------------------------------------------------

const PATH_TITLE_KEYS: [string, string][] = [
  ['/dashboard/calendar', 'nav.calendar'],
  ['/dashboard/clients', 'nav.clients'],
  ['/dashboard/services', 'nav.services'],
  ['/dashboard/staff', 'nav.staff'],
  ['/dashboard/settings', 'nav.settings'],
  ['/dashboard/analytics', 'nav.analytics'],
  ['/dashboard/reminders', 'nav.reminders'],
  ['/dashboard/reviews', 'nav.reviews'],
  ['/dashboard/ai', 'nav.ai'],
  ['/dashboard', 'dashboard.title'],
];

function getTitle(pathname: string, t: (key: string) => string): string {
  for (const [path, key] of PATH_TITLE_KEYS) {
    if (pathname === path || pathname.startsWith(path + '/')) {
      return t(key);
    }
  }
  return t('dashboard.title');
}

// ---------------------------------------------------------------------------
// TopBar
// ---------------------------------------------------------------------------

export interface TopBarProps {
  pathname: string;
  onLogout: () => void;
}

export function TopBar({ pathname, onLogout }: TopBarProps) {
  const { t } = useTranslation();
  const title = getTitle(pathname, t);

  return (
    <header
      style={{
        height: 64,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingInline: '1.5rem',
        background: 'var(--bg-surface)',
        borderBlockEnd: '1px solid var(--border-subtle)',
        flexShrink: 0,
        gap: '1rem',
      }}
    >
      {/* Start (right in RTL): title + stealth badge */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        {/* Styled div — NOT a heading to avoid duplicate-heading clash with page <h1> */}
        <div
          data-testid="topbar-title"
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-title2)',
            fontWeight: 600,
            color: 'var(--text-primary)',
            lineHeight: 'var(--lh-title2)',
          }}
        >
          {title}
        </div>
        {/* Stealth badge — always shown (stealth/unpublished is the default state) */}
        <Badge tone="neutral" dot data-testid="stealth-badge">
          {t('nav.stealth_mode')}
        </Badge>
      </div>

      {/* End (left in RTL): language toggle + logout */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <LanguageToggle
          style={{
            background: 'transparent',
            border: '1px solid var(--border-strong)',
            color: 'var(--text-secondary)',
            padding: '0.25rem 0.625rem',
            borderRadius: 'var(--r-md)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-caption)',
            cursor: 'pointer',
          }}
        />
        <button
          type="button"
          onClick={onLogout}
          style={{
            background: 'transparent',
            border: '1px solid var(--border-strong)',
            color: 'var(--text-secondary)',
            padding: '0.25rem 0.625rem',
            borderRadius: 'var(--r-md)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-caption)',
            cursor: 'pointer',
          }}
        >
          {t('common.logout')}
        </button>
      </div>
    </header>
  );
}
